import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { User, Package, Heart, FileText, Bell, Settings, LogOut, ArrowRight } from "lucide-react";

function SavedMaterialsCard() {
    const { ids, toggle } = useFavorites();
    const [items, setItems] = useState([]);

    useEffect(() => {
        if (!ids.length) {
            setItems([]);
            return;
        }
        api.get("/favorites")
            .then((res) => setItems(res.data))
            .catch(() => {});
    }, [ids]);

    return (
        <div
            data-testid="account-section-saved-materials"
            className="card-lift h-full bg-white border border-stone-200 rounded-sm p-7 hover:border-gold/50"
        >
            <Heart className="h-6 w-6 text-gold" strokeWidth={1.5} />
            <h2 className="mt-4 font-serif text-xl font-light text-brand">Saved Materials</h2>
            {items.length === 0 ? (
                <p className="mt-3 text-xs leading-relaxed text-mist">
                    No saved materials yet — tap the heart on any product card to save it here.
                </p>
            ) : (
                <ul className="mt-4 space-y-2.5" data-testid="saved-materials-list">
                    {items.map((item) => (
                        <li key={item.id} className="flex items-center justify-between gap-3 text-xs">
                            <Link
                                to={`/materials/${item.id}`}
                                className="text-slate-700 hover:text-gold transition-colors duration-300 truncate"
                            >
                                {item.name}
                            </Link>
                            <button
                                type="button"
                                aria-label={`Remove ${item.name}`}
                                data-testid={`saved-remove-${item.id}`}
                                onClick={() => toggle(item.id)}
                                className="text-gold shrink-0"
                            >
                                <Heart className="h-3.5 w-3.5 fill-current" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function OrdersCard() {
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        api.get("/orders/mine")
            .then((res) => setOrders(res.data))
            .catch(() => {});
    }, []);

    const STATUS_LABELS = {
        pending_review: "Pending review",
        awaiting_payment: "Awaiting payment",
        paid: "Paid",
        packing: "Packing",
        ready_to_ship: "Ready to ship",
        shipped: "Shipped",
        cancelled: "Cancelled",
    };

    return (
        <div
            data-testid="account-section-order-history"
            className="card-lift h-full bg-white border border-stone-200 rounded-sm p-7 hover:border-gold/50"
        >
            <Package className="h-6 w-6 text-gold" strokeWidth={1.5} />
            <h2 className="mt-4 font-serif text-xl font-light text-brand">Order History</h2>
            {orders.length === 0 ? (
                <p className="mt-3 text-xs leading-relaxed text-mist">
                    No orders yet — your orders and their payment status will appear here.
                </p>
            ) : (
                <ul className="mt-4 space-y-3" data-testid="account-orders-list">
                    {orders.map((o) => (
                        <li key={o.order_id} className="text-xs" data-testid={`account-order-${o.order_id}`}>
                            <div className="flex items-center justify-between gap-3">
                                <span className="font-semibold text-brand">{o.order_id}</span>
                                <span className="text-slate-700">${(o.total_cents / 100).toFixed(2)}</span>
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-3">
                                <span className="text-mist">
                                    {new Date(o.created_at).toLocaleDateString()} ·{" "}
                                    <span className={o.status === "paid" ? "text-emerald-700" : o.status === "awaiting_payment" ? "text-blue-700" : "text-amber-700"}>
                                        {STATUS_LABELS[o.status] || o.status}
                                    </span>
                                </span>
                                {o.tracking_number && (
                                    <p className="mt-1 text-[0.65rem] text-slate-500" data-testid={`account-tracking-${o.order_id}`}>
                                        Tracking: <span className="font-semibold text-slate-700">{o.tracking_number}</span>
                                    </p>
                                )}
                                {o.status === "awaiting_payment" && o.payment_link && (
                                    <a
                                        href={o.payment_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`account-pay-${o.order_id}`}
                                        className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.18em] font-semibold text-wine hover:text-wine-deep transition-colors"
                                    >
                                        Pay now <ArrowRight className="h-3 w-3" />
                                    </a>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function Account() {
    const { user, logout, updateMarketing } = useAuth();
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);

    if (!user) return null;

    const memberSince = user.created_at
        ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long" })
        : "";

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const handleMarketing = async (checked) => {
        setSaving(true);
        try {
            await updateMarketing(checked);
            toast.success("Preference saved", {
                description: checked
                    ? "You will receive Grayhaven Research updates by email."
                    : "You have been unsubscribed from marketing emails.",
            });
        } catch {
            toast.error("Could not save preference", { description: "Please try again." });
        } finally {
            setSaving(false);
        }
    };

    const SECTIONS = [
        { icon: FileText, title: "Documentation Access", text: "Certificates of Analysis and laboratory documentation for your materials, in one place.", link: { to: "/testing", label: "Open Testing & COAs" } },
        { icon: Bell, title: "Notifications", text: "New-material announcements and documentation updates will appear here." },
    ];
    if (user.role === "admin") {
        SECTIONS.push({ icon: Settings, title: "Administration", text: "Review orders, send payment links, and configure checkout.", link: { to: "/admin", label: "Open Admin Panel" } });
    }

    return (
        <div data-testid="account-page">
            <PageHeader
                eyebrow="My Grayhaven"
                title={`Welcome, ${user.first_name}`}
                description="Your Grayhaven Research account — profile, documentation and preferences in one place."
            />

            <section className="pb-24 sm:pb-32 px-6 lg:px-8">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Profile */}
                    <Reveal className="lg:col-span-4">
                        <div className="bg-white border border-stone-200 rounded-sm p-8 h-full" data-testid="account-profile-card">
                            <div className="flex items-center gap-4">
                                <span className="h-14 w-14 rounded-full bg-surface2 border border-gold/40 flex items-center justify-center">
                                    <User className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                </span>
                                <div>
                                    <p className="font-serif text-xl text-brand">
                                        {user.first_name} {user.last_name}
                                    </p>
                                    <p className="text-xs text-mist mt-0.5">{user.email}</p>
                                </div>
                            </div>
                            <div className="mt-6 pt-6 border-t border-stone-200 space-y-2 text-xs text-slate-500">
                                {memberSince && <p>Member since {memberSince}</p>}
                                <p>Marketing updates: {user.marketing_consent ? "Subscribed" : "Not subscribed"}</p>
                            </div>
                            <button
                                type="button"
                                onClick={handleLogout}
                                data-testid="account-signout-btn"
                                className="mt-8 inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-gold transition-colors duration-300"
                            >
                                <LogOut className="h-3.5 w-3.5" /> Sign Out
                            </button>
                        </div>
                    </Reveal>

                    {/* Sections */}
                    <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <Reveal delay={0.05}>
                            <SavedMaterialsCard />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <OrdersCard />
                        </Reveal>
                        {SECTIONS.map((section, i) => (
                            <Reveal key={section.title} delay={i * 0.07}>
                                <div
                                    data-testid={`account-section-${section.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="card-lift h-full bg-white border border-stone-200 rounded-sm p-7 hover:border-gold/50"
                                >
                                    <section.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                    <h2 className="mt-4 font-serif text-xl font-light text-brand">{section.title}</h2>
                                    <p className="mt-3 text-xs leading-relaxed text-mist">{section.text}</p>
                                    {section.link && (
                                        <Link
                                            to={section.link.to}
                                            data-testid="account-docs-link"
                                            className="mt-4 inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-wine hover:text-wine-deep transition-colors duration-300"
                                        >
                                            {section.link.label} <ArrowRight className="h-3.5 w-3.5" />
                                        </Link>
                                    )}
                                </div>
                            </Reveal>
                        ))}

                        {/* Settings */}
                        <Reveal delay={0.3} className="sm:col-span-2">
                            <div className="bg-white border border-stone-200 rounded-sm p-7" data-testid="account-settings">
                                <div className="flex items-center gap-3">
                                    <Settings className="h-6 w-6 text-gold" strokeWidth={1.5} />
                                    <h2 className="font-serif text-xl font-light text-brand">Account Settings</h2>
                                </div>
                                <label className="mt-6 flex items-start gap-3 cursor-pointer max-w-xl" data-testid="account-marketing-toggle">
                                    <input
                                        type="checkbox"
                                        disabled={saving}
                                        checked={!!user.marketing_consent}
                                        onChange={(e) => handleMarketing(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#7A2230]"
                                    />
                                    <span className="text-xs leading-relaxed text-slate-600">
                                        Send me Grayhaven Research updates, new-material announcements, and other
                                        marketing communications by email. You can change this at any time.
                                    </span>
                                </label>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
